import { useCallback, useEffect, useMemo, useState } from 'react'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { getAuthToken, useSupabaseAuth } from '@/lib/supabase'

export type UseCreditsResult = {
  credits: number | null
  isLoading: boolean
  refetch: () => Promise<void>
  hasEnoughCredits: (minRequired?: number) => boolean
}

export function useCredits(): UseCreditsResult {
  const { isAuthenticated } = useSupabaseAuth()
  const [credits, setCredits] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const refetch = useCallback(async () => {
    if (!isAuthenticated) {
      setCredits(null)
      return
    }

    const token = await getAuthToken()
    if (!token || !BACKEND_URL) return

    setIsLoading(true)
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Fetch balance (useCredits)')
        }
        return
      }

      const data = (await response.json()) as { credits?: unknown }
      const nextCredits = Number(data.credits ?? 0)
      setCredits(Number.isFinite(nextCredits) ? nextCredits : 0)
    } catch (error) {
      console.warn('[useCredits] Failed to fetch balance', error)
      // Fail-open: UI can still work; server enforces in /api/agent/gk.
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAuthenticated) {
      setCredits(null)
      return
    }

    let isCancelled = false
    void (async () => {
      if (isCancelled) return
      await refetch()
    })()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refetch()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      isCancelled = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isAuthenticated])

  const hasEnoughCredits = useCallback(
    (minRequired = 0.01) =>
      credits !== null && Number.isFinite(credits) && credits >= minRequired,
    [credits]
  )

  return useMemo(
    () => ({
      credits,
      isLoading,
      refetch,
      hasEnoughCredits,
    }),
    [credits, hasEnoughCredits, isLoading, refetch]
  )
}
